import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, RefreshCw, X, Loader2, Save, Link2, Unlink, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  efAdminFragmentsList,
  efAdminFragmentCreate,
  efAdminFragmentPatch,
  efAdminFragmentDelete,
  efAdminFragmentAttach,
  efAdminFragmentDetach,
  efAdminListEntities,
} from "../../lib/api";
import client from "../../lib/api";

/**
 * AdminEpsteinFragments — manage evidence fragments under sources.
 *
 *   • List + filter (by source / visibility)
 *   • Create with full email / excerpt fields
 *   • Edit visibility + content
 *   • Attach to entities / relationships / timeline events / evidence items
 *
 * Designed to be embedded as the "Fragments" tab in /admin/epstein.
 */

const FRAGMENT_TYPES = [
  "email", "document_excerpt", "signature_page", "payment_clause",
  "timeline_reference", "news_quote", "press_release_excerpt",
  "court_excerpt", "other",
];
const QUOTE_CONF = ["", "verbatim", "paraphrase", "summary"];
const VISIBILITY = ["hold", "review", "public"];

const VIS_COLORS = {
  hold:   "text-[#757065] border-[#454038]",
  review: "text-[#E0A526] border-[#E0A526]/40",
  public: "text-[#5DD4B3] border-[#5DD4B3]/40",
};

const blankFragment = (defaults = {}) => ({
  source_id: "",
  title: "",
  fragment_type: "document_excerpt",
  page_number: "",
  anchor_text: "",
  summary: "",
  document_date: "",
  sender: "",
  recipient: "",
  url_override: "",
  visibility_status: "review",
  quote_confidence: "",
  editor_note: "",
  ...defaults,
});

export const AdminEpsteinFragments = ({ token, userToken = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ source_id: "", visibility: "" });
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blankFragment());
  const [editingId, setEditingId] = useState(null);
  const [sources, setSources] = useState([]);
  const [entities, setEntities] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.source_id) params.source_id = filter.source_id;
      if (filter.visibility) params.visibility = filter.visibility;
      const r = await efAdminFragmentsList(token, params, userToken);
      setRows(r.fragments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load fragments");
    } finally { setLoading(false); }
  }, [token, userToken, filter]);

  useEffect(() => { load(); }, [load]);

  // Load all sources + entities once for the dropdowns.
  useEffect(() => {
    (async () => {
      try {
        const auth = userToken
          ? { Authorization: `Bearer ${userToken}`, "X-Admin-Token": token }
          : { "X-Admin-Token": token };
        const r = await client.get("/admin/epstein/sources", { headers: auth });
        setSources(r.data.rows || []);
      } catch { /* silent */ }
      try {
        const r = await efAdminListEntities(token, { limit: 500 }, userToken);
        setEntities(r.rows || []);
      } catch { /* silent */ }
    })();
  }, [token, userToken]);

  const submitCreate = async () => {
    if (!draft.source_id) { toast.error("Pick a parent source."); return; }
    if (!draft.title || draft.title.length < 2) { toast.error("Title is required."); return; }
    const payload = { ...draft };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    try {
      const r = await efAdminFragmentCreate(token, payload, userToken);
      toast.success(`Fragment ${r.id} created`);
      setCreating(false);
      setDraft(blankFragment());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const savePatch = async (id, partial) => {
    try {
      await efAdminFragmentPatch(token, id, partial, userToken);
      toast.success("Saved");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Patch failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this fragment? Attachments will be lost.")) return;
    try {
      await efAdminFragmentDelete(token, id, userToken);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const attach = async (id, target) => {
    try {
      await efAdminFragmentAttach(token, id, target, userToken);
      toast.success("Attached");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    }
  };

  const detach = async (id, target) => {
    try {
      await efAdminFragmentDetach(token, id, target, userToken);
      toast.success("Detached");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detach failed");
    }
  };

  return (
    <div data-testid="ef-admin-fragments-panel">
      {/* Header / Filters */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <FileText size={11} /> // EVIDENCE FRAGMENTS
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Exact-anchor evidence</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Emails, page references, signature pages, payment clauses, and reporting excerpts that
            anchor specific pieces of the graph to specific locations inside their parent sources.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={filter.source_id}
            onChange={(e) => setFilter((f) => ({ ...f, source_id: e.target.value }))}
            className="border border-[#454038] bg-[#0F0F0F] text-[#A39E93] px-2 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            data-testid="ef-fragments-filter-source"
          >
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>{(s.publisher || "—")} · {s.title?.slice(0, 60)}</option>
            ))}
          </select>
          <select
            value={filter.visibility}
            onChange={(e) => setFilter((f) => ({ ...f, visibility: e.target.value }))}
            className="border border-[#454038] bg-[#0F0F0F] text-[#A39E93] px-2 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            data-testid="ef-fragments-filter-visibility"
          >
            <option value="">All visibility</option>
            {VISIBILITY.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <button onClick={load} data-testid="ef-fragments-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => { setDraft(blankFragment({ source_id: filter.source_id })); setCreating(true); }}
            data-testid="ef-fragments-new"
            className="flex items-center gap-2 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Plus size={11} /> New fragment
          </button>
        </div>
      </div>

      {creating && (
        <FragmentForm
          draft={draft}
          setDraft={setDraft}
          sources={sources}
          onCancel={() => setCreating(false)}
          onSubmit={submitCreate}
          submitLabel="Create"
        />
      )}

      {/* Table */}
      <div className="border border-[#2D2A26] bg-[#131313]">
        {loading && (
          <div className="p-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] flex items-center gap-2">
            <Loader2 size={11} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
               data-testid="ef-fragments-empty">
            No fragments match the current filters.
          </div>
        )}
        {!loading && rows.map((f) => (
          <FragmentRow
            key={f.id}
            f={f}
            entities={entities}
            editing={editingId === f.id}
            onEdit={() => setEditingId(f.id)}
            onCancel={() => setEditingId(null)}
            onSave={(partial) => savePatch(f.id, partial)}
            onDelete={() => remove(f.id)}
            onAttach={(t) => attach(f.id, t)}
            onDetach={(t) => detach(f.id, t)}
          />
        ))}
      </div>
    </div>
  );
};


// ---------------- internals ----------------

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">{label}</span>
    {children}
  </label>
);

const TextInput = (props) => (
  <input
    {...props}
    className={
      "w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono " +
      (props.className || "")
    }
  />
);
const TextArea = (props) => (
  <textarea
    {...props}
    className={
      "w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono " +
      (props.className || "")
    }
  />
);

const FragmentForm = ({ draft, setDraft, sources, onSubmit, onCancel, submitLabel }) => {
  const upd = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const isEmail = draft.fragment_type === "email";
  return (
    <div className="border border-[#D92D20]/40 bg-[#0F0F0F] p-4 mb-3" data-testid="ef-fragments-form">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {submitLabel} fragment</div>
        <button onClick={onCancel} className="text-[#757065] hover:text-[#E4DFD3]" data-testid="ef-fragments-form-cancel">
          <X size={14} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="parent source *" testid="ef-frag-field-source">
          <select value={draft.source_id} onChange={upd("source_id")}
            className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono"
            data-testid="ef-frag-source-select">
            <option value="">— pick source —</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>{(s.publisher || "—")} · {s.title?.slice(0, 70)}</option>
            ))}
          </select>
        </Field>
        <Field label="fragment type *">
          <select value={draft.fragment_type} onChange={upd("fragment_type")}
            className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono">
            {FRAGMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="title *">
          <TextInput value={draft.title} onChange={upd("title")} data-testid="ef-frag-title" />
        </Field>
        <Field label="document date (e.g. 2015-10-05)">
          <TextInput value={draft.document_date} onChange={upd("document_date")} placeholder="YYYY-MM-DD or YYYY-MM or YYYY" />
        </Field>
        <Field label="page / locator (e.g. 'p. 4' or 'pp. 12-14')">
          <TextInput value={draft.page_number} onChange={upd("page_number")} placeholder="Leave blank if not known" />
        </Field>
        <Field label="quote confidence">
          <select value={draft.quote_confidence} onChange={upd("quote_confidence")}
            className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono">
            {QUOTE_CONF.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
          </select>
        </Field>
        {isEmail && (
          <>
            <Field label="from (sender)">
              <TextInput value={draft.sender} onChange={upd("sender")} placeholder="e.g. Jeffrey Epstein" />
            </Field>
            <Field label="to (recipient)">
              <TextInput value={draft.recipient} onChange={upd("recipient")} placeholder="e.g. Ariane de Rothschild" />
            </Field>
          </>
        )}
        <Field label="deep-link override (optional)">
          <TextInput value={draft.url_override} onChange={upd("url_override")}
            placeholder="Used only if a stable per-page or per-anchor URL exists." />
        </Field>
        <Field label="visibility">
          <select value={draft.visibility_status} onChange={upd("visibility_status")}
            className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono"
            data-testid="ef-frag-visibility">
            {VISIBILITY.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="anchor text / verbatim excerpt">
            <TextArea rows={3} value={draft.anchor_text} onChange={upd("anchor_text")}
              placeholder="Paste the exact excerpt from the source." />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="summary">
            <TextArea rows={2} value={draft.summary} onChange={upd("summary")} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="editor note (shown publicly when set)">
            <TextArea rows={2} value={draft.editor_note} onChange={upd("editor_note")}
              placeholder="e.g. Sourced via Miami Herald reproduction; underlying DOJ file not yet anchored." />
          </Field>
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onCancel}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] border border-[#454038] px-3 py-2">
          Cancel
        </button>
        <button onClick={onSubmit} data-testid="ef-frag-submit"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] border border-[#D92D20] hover:bg-[#1a0808] px-3 py-2 flex items-center gap-1">
          <Save size={11} /> {submitLabel}
        </button>
      </div>
    </div>
  );
};

const FragmentRow = ({ f, entities, editing, onEdit, onCancel, onSave, onDelete, onAttach, onDetach }) => {
  const [draft, setDraft] = useState(() => ({ ...f, page_number: f.page_number || "", anchor_text: f.anchor_text || "", summary: f.summary || "", editor_note: f.editor_note || "", url_override: f.url_override || "", document_date: f.document_date || "", sender: f.sender || "", recipient: f.recipient || "" }));
  const [attachKind, setAttachKind] = useState("entity");
  const [attachId, setAttachId] = useState("");

  useEffect(() => {
    setDraft({ ...f, page_number: f.page_number || "", anchor_text: f.anchor_text || "", summary: f.summary || "", editor_note: f.editor_note || "", url_override: f.url_override || "", document_date: f.document_date || "", sender: f.sender || "", recipient: f.recipient || "" });
  }, [f, editing]);

  const visClass = VIS_COLORS[f.visibility_status] || "text-[#757065] border-[#454038]";
  const ents = f.attached_entity_ids || [];
  const rels = f.attached_relationship_ids || [];
  const tls  = f.attached_timeline_event_ids || [];

  const onSubmitAttach = () => {
    if (!attachId.trim()) return;
    const k = attachKind === "entity" ? "entity_id"
            : attachKind === "relationship" ? "relationship_id"
            : attachKind === "timeline" ? "timeline_event_id"
            : "evidence_item_id";
    onAttach({ [k]: attachId.trim() });
    setAttachId("");
  };

  return (
    <div className="border-b border-[#2D2A26] px-3 py-3" data-testid={`ef-fragment-row-${f.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className={`font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 ${visClass}`}>
          {f.visibility_status}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#CBB892] border border-[#CBB892]/40 px-1.5 py-0.5">
          {f.fragment_type}
        </span>
        {f.page_number && (
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] border border-[#454038] px-1.5 py-0.5">{f.page_number}</span>
        )}
        {f.document_date && (
          <span className="font-mono text-[9px] text-[#757065]">{f.document_date}</span>
        )}
        <span className="ml-auto font-mono text-[9px] text-[#757065]">{f.id}</span>
      </div>

      {!editing ? (
        <>
          <div className="font-heading text-sm text-[#E4DFD3] mb-1">{f.title}</div>
          {f.anchor_text && (
            <blockquote className="border-l-2 border-[#D92D20]/60 pl-2 italic text-xs text-[#A39E93] my-1 line-clamp-3">
              “{f.anchor_text}”
            </blockquote>
          )}
          {(f.sender || f.recipient) && (
            <div className="font-mono text-[10px] text-[#A39E93]">
              {f.sender && <><span className="text-[#757065]">from</span> {f.sender} </>}
              {f.recipient && <><span className="text-[#757065]">to</span> {f.recipient}</>}
            </div>
          )}
          <div className="font-mono text-[10px] text-[#757065] mt-1">
            source: {f.source_id} · attached: {ents.length} entities · {rels.length} relationships · {tls.length} timeline events
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={onEdit}
              data-testid={`ef-fragment-edit-${f.id}`}
              className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2 py-1">
              Edit
            </button>
            <button onClick={onDelete}
              data-testid={`ef-fragment-delete-${f.id}`}
              className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#D92D20]/60 hover:bg-[#1a0808] text-[#D92D20] px-2 py-1 flex items-center gap-1">
              <Trash2 size={9} /> Delete
            </button>
            <select value={attachKind} onChange={(e) => setAttachKind(e.target.value)}
              className="bg-[#0F0F0F] border border-[#454038] text-[#A39E93] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em]">
              <option value="entity">attach → entity</option>
              <option value="relationship">attach → relationship</option>
              <option value="timeline">attach → timeline</option>
              <option value="evidence">attach → evidence item</option>
            </select>
            {attachKind === "entity" ? (
              <select value={attachId} onChange={(e) => setAttachId(e.target.value)}
                data-testid={`ef-fragment-attach-entity-select-${f.id}`}
                className="bg-[#0F0F0F] border border-[#454038] text-[#A39E93] px-2 py-1 font-mono text-[9px]">
                <option value="">— pick entity —</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.kind})</option>)}
              </select>
            ) : (
              <input value={attachId} onChange={(e) => setAttachId(e.target.value)}
                placeholder={`paste ${attachKind} id`}
                className="bg-[#0F0F0F] border border-[#454038] text-[#A39E93] px-2 py-1 font-mono text-[9px] flex-1 min-w-[10rem]" />
            )}
            <button onClick={onSubmitAttach}
              data-testid={`ef-fragment-attach-${f.id}`}
              className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#5DD4B3]/60 hover:bg-[#0e1816] text-[#5DD4B3] px-2 py-1 flex items-center gap-1">
              <Link2 size={9} /> Attach
            </button>
          </div>
          {/* Show attached + provide detach buttons */}
          {(ents.length + rels.length + tls.length) > 0 && (
            <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
              {ents.length > 0 && (
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">entities</div>
                  {ents.map((id) => (
                    <DetachChip key={id} id={id} onDetach={() => onDetach({ entity_id: id })} />
                  ))}
                </div>
              )}
              {rels.length > 0 && (
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">relationships</div>
                  {rels.map((id) => (
                    <DetachChip key={id} id={id} onDetach={() => onDetach({ relationship_id: id })} />
                  ))}
                </div>
              )}
              {tls.length > 0 && (
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">timeline</div>
                  {tls.map((id) => (
                    <DetachChip key={id} id={id} onDetach={() => onDetach({ timeline_event_id: id })} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <FragmentForm
          draft={draft}
          setDraft={setDraft}
          sources={[{ id: f.source_id, title: "(parent unchanged)", publisher: "" }]}
          onCancel={onCancel}
          onSubmit={() => {
            const partial = {};
            ["title", "fragment_type", "page_number", "anchor_text", "summary", "document_date",
              "sender", "recipient", "url_override", "visibility_status", "quote_confidence", "editor_note"
            ].forEach((k) => {
              if (draft[k] !== f[k]) partial[k] = draft[k] === "" ? null : draft[k];
            });
            if (Object.keys(partial).length === 0) { toast.info("Nothing changed"); return; }
            onSave(partial);
          }}
          submitLabel="Save changes"
        />
      )}
    </div>
  );
};

const DetachChip = ({ id, onDetach }) => (
  <div className="inline-flex items-center gap-1 border border-[#454038] px-2 py-1 mr-1 mb-1">
    <span className="font-mono text-[9px] text-[#A39E93] truncate max-w-[12rem]">{id}</span>
    <button onClick={onDetach} className="text-[#757065] hover:text-[#D92D20]" title="detach">
      <Unlink size={9} />
    </button>
  </div>
);

export default AdminEpsteinFragments;
